import React from 'react';
import type { ICellRendererParams } from 'ag-grid-community';

export interface ActiveInactiveCellRendererProps extends ICellRendererParams {
	/**
	 * The field name to read the active state from if different from 'value'
	 * @default uses params.value
	 */
	activeField?: string;
	/**
	 * Text to display when active
	 * @default "Active"
	 */
	activeLabel?: string;
	/**
	 * Text to display when inactive
	 * @default "Inactive"
	 */
	inactiveLabel?: string;
	/**
	 * Whether to show as a badge style (rounded pill) or simple text
	 * @default true
	 */
	showAsBadge?: boolean;
}

/**
 * A reusable cell renderer for displaying active/inactive states in ag-Grid.
 * Provides consistent styling across all lists that show entity status.
 *
 * Usage:
 * ```tsx
 * {
 *   headerName: 'Status',
 *   field: 'isActive',
 *   cellRenderer: ActiveInactiveCellRenderer,
 *   cellRendererParams: {
 *     activeLabel: 'Enabled',
 *     inactiveLabel: 'Disabled'
 *   }
 * }
 * ```
 */
export const ActiveInactiveCellRenderer: React.FC<ActiveInactiveCellRendererProps> = ({
	value,
	data,
	activeField,
	activeLabel = 'Active',
	inactiveLabel = 'Inactive',
	showAsBadge = true
}) => {
	// Determine the active state from value or specified field
	let isActive = value;
	if (activeField && data) {
		isActive = data[activeField];
	}

	// Convert to boolean for consistent behavior
	const active = Boolean(isActive);

	if (showAsBadge) {
		return (
			<span
				className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
					active
						? 'bg-transparent border-gray-300 text-gray-600 dark:border-gray-600 dark:text-gray-400'
						: 'bg-gray-100 border-gray-200 text-gray-600 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400'
				}`}
			>
				{active ? activeLabel : inactiveLabel}
			</span>
		);
	}

	// Simple text version without badge styling
	return (
		<span
			className={`text-sm font-medium ${
				active
					? 'text-gray-600 dark:text-gray-400'
					: 'text-gray-600 dark:text-gray-400'
			}`}
		>
			{active ? activeLabel : inactiveLabel}
		</span>
	);
};
